import { z } from "zod";

// Markets (Marchés): countries a client sells in, with a shipping fee table per zone (governorate for Tunisia).
// Amounts are integer minor units in the client currency.

const minor = z.number().int("Montant invalide").min(0, "Montant invalide");
const etaDays = z.number().int("Délai invalide").min(0, "Délai invalide").max(60, "60 jours maximum");

export const marketZoneFeeSchema = z.object({
  zone: z.string().trim().min(1, "Zone requise").max(80),
  feeMinor: minor,
  etaDays: etaDays.optional().default(2),
});
export type MarketZoneFeeInput = z.infer<typeof marketZoneFeeSchema>;

export const marketShippingSchema = z.object({
  defaultFeeMinor: minor,
  freeShippingThresholdMinor: minor.nullable(),
  feesByZone: z.array(marketZoneFeeSchema).max(300, "300 zones maximum"),
  codEnabled: z.boolean(),
  defaultEtaDays: etaDays,
});
export type MarketShippingInput = z.infer<typeof marketShippingSchema>;

export const marketCreateSchema = z.object({
  code: z.string().trim().length(2, "Code pays invalide").toUpperCase(),
  enabled: z.boolean().optional().default(true),
  shipping: marketShippingSchema.partial().optional(),
});
export type MarketCreateInput = z.infer<typeof marketCreateSchema>;

export const marketUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  shipping: marketShippingSchema.partial().optional(),
});
export type MarketUpdateInput = z.infer<typeof marketUpdateSchema>;

export const shippingQuoteQuery = z.object({
  marketCode: z.string().trim().length(2).toUpperCase().optional(),
  governorate: z.string().trim().max(80).optional().default(""),
  subtotalMinor: z.coerce.number().int().min(0).optional().default(0),
});
export type ShippingQuoteQuery = z.infer<typeof shippingQuoteQuery>;

export interface MarketDto {
  id: string;
  code: string;
  name: string;
  currency: string;
  timezone: string;
  locale: string;
  phoneCountry: string;
  dialCode: string;
  enabled: boolean;
  isDefault: boolean;
  shipping: {
    defaultFeeMinor: number;
    freeShippingThresholdMinor: number | null;
    feesByZone: Array<{ zone: string; feeMinor: number; etaDays: number }>;
    codEnabled: boolean;
    defaultEtaDays: number;
  };
  createdAt: string;
  updatedAt: string;
}

/** Shipping fee for one destination, computed from the market fee table. */
export interface ShippingQuoteDto {
  marketCode: string;
  feeMinor: number;
  /** Fee before the free shipping threshold is applied. */
  baseFeeMinor: number;
  freeShipping: boolean;
  /** Zone of the fee table that matched the destination, null when the default fee applies. */
  zone: string | null;
  etaDays: number;
  expectedDeliveryAt: string;
  codEnabled: boolean;
  currency: string;
}
